import React, { useState } from 'react';
import { 
  Box, 
  Drawer, 
  AppBar, 
  Toolbar, 
  List, 
  Typography, 
  Divider, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  IconButton,
  Avatar,
  Badge,
  useTheme,
  styled,
  Tooltip,
  Menu,
  MenuItem,
  Button
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import AnimatedOutlet from './AnimatedOutlet';
import PetsIcon from '@mui/icons-material/Pets';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MedicationIcon from '@mui/icons-material/Medication';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationBell from '../NotificationBell/NotificationBell';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import EmailIcon from '@mui/icons-material/Email';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import PaidIcon from '@mui/icons-material/Paid';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SecurityIcon from '@mui/icons-material/Security';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../contexts/AuthContext';
import { usePets, Pet } from '../../contexts/PetContext';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import AddToHomeScreen from '../AddToHomeScreen';
import OfflineIndicator from '../OfflineIndicator';
import Logo from '../Logo';
import MobileBottomNavigation from './MobileBottomNavigation';
import EmailVerificationBanner from '../EmailVerificationBanner';
import { alpha } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import { getSafeAreaTop } from '../../hooks/useSafeArea';

const drawerWidth = 260;

const StyledListItem = styled(ListItem)<{ active?: boolean; isSubItem?: boolean }>(({ theme, active, isSubItem: _isSubItem }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'transparent',
  position: 'relative',
  // Glossy bottom accent bar
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: theme.spacing(2),
    right: theme.spacing(2),
    height: active ? '2px' : '0px',
    background: active 
      ? `linear-gradient(90deg, 
          transparent 0%, 
          ${alpha(theme.palette.primary.main, 0.4)} 20%, 
          ${theme.palette.primary.main} 50%, 
          ${alpha(theme.palette.primary.main, 0.4)} 80%, 
          transparent 100%)`
      : 'transparent',
    borderRadius: '2px',
    boxShadow: active 
      ? `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}, 
         0 0 4px ${alpha(theme.palette.primary.main, 0.3)}`
      : 'none',
    transition: 'all 0.3s ease',
  },
  '& .MuiListItemIcon-root': {
    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  },
  '& .MuiListItemText-primary': {
    color: active ? theme.palette.primary.main : theme.palette.text.primary,
    fontWeight: active ? 600 : 400,
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.action.hover, 0.05),
    '&::after': {
      height: '1px',
      background: `linear-gradient(90deg, 
        transparent 0%, 
        ${alpha(theme.palette.primary.main, 0.2)} 50%, 
        transparent 100%)`,
    },
  },
  transition: 'all 0.2s ease',
}));

// Simplified Navigation Structure - Option A: Health Intelligence → Financial → Logistics → Resources
const getMainNavItems = (_selectedPetId?: string) => [
  {
    text: 'Home',
    icon: <HomeRoundedIcon />,
    path: '/home',
    description: 'Quick actions, today\'s doses & recent activity'
  },
  {
    text: 'Health Insights',
    icon: <HealthAndSafetyIcon />,
    path: '/health',
    description: 'Overview, Timeline & Actions'
  },
  { 
    text: 'Care Plan', 
    icon: <MedicationIcon />, 
    path: '/care-plan',
    description: 'Your pet\'s ongoing care & medications'
  },
  { 
    text: 'Pet Expenses', 
    icon: <PaidIcon />, 
    path: '/pet-expenses',
    description: 'Track spending & insurance claims'
  },
  { 
    text: 'Appointments', 
    icon: <CalendarMonthIcon />, 
    path: '/calendar',
    description: 'Upcoming visits & reminders'
  },
  { 
    text: 'Messages', 
    icon: <EmailIcon />, 
    path: '/inbox',
    description: 'Messages with your care team'
  },
  {
    text: 'Care Team',
    icon: <MedicalServicesIcon />,
    path: '/care-team',
    description: "All your pet's providers"
  },
  {
    text: 'Ask Suprie',
    icon: <AutoAwesomeIcon sx={{ color: '#A855F7' }} />,
    path: '/ask-suprie',
    description: 'AI-powered pet health assistant'
  }
];

// Removed submenu items - using simplified navigation

interface LayoutProps {
  hideLogo?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ hideLogo = false }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Removed submenu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [petMenuAnchor, setPetMenuAnchor] = useState<null | HTMLElement>(null);
  const { currentUser, logout } = useAuth();
  const { selectedPet, pets, setSelectedPet } = usePets();
  const { isAdmin, loading: adminLoading } = useAdminAuth();
  const theme = useTheme();
  const { isDarkMode, toggleTheme } = useAppTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Get navigation items with dynamic pet path
  const mainNavItems = getMainNavItems(selectedPet?.id);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Removed submenu toggle function

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    handleMenuClose();
  };

  const isMenuOpen = Boolean(anchorEl);

  const handlePetMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPetMenuAnchor(event.currentTarget);
  };

  const handlePetMenuClose = () => {
    setPetMenuAnchor(null);
  };

  const handlePetSwitch = (pet: Pet) => {
    // Update sessionStorage
    sessionStorage.setItem('selectedPetId', pet.id);
    sessionStorage.setItem('selectedPetName', pet.name);
    
    // Update context
    setSelectedPet(pet);
    
    // Close menu
    handlePetMenuClose();
  };

  const isPetMenuOpen = Boolean(petMenuAnchor);

  const drawer = (
    <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Main Navigation */}
      <List component="nav" sx={{ px: 1, py: 1 }}>
        {mainNavItems.map((item) => (
          <React.Fragment key={item.text}>
            <StyledListItem 
              disablePadding 
              active={
                item.text === 'Health Insights'
                  ? (location.pathname === '/health' || location.pathname === '/health-insights')
                  : location.pathname === item.path
              } 
              isSubItem={false}
            >
              <ListItemButton 
                onClick={() => handleNavigation(item.path)}
                sx={{ 
                  borderRadius: 2, 
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: theme => alpha(theme.palette.primary.main, 0.08),
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease-in-out',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: '48px' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  secondary={item.description}
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: 500
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.82rem',
                    color: 'text.secondary',
                    sx: { 
                      mt: 0.5,
                      lineHeight: 1.3,
                      display: 'block'  // Ensure it's always visible
                    }
                  }}
                />
                {/* Removed submenu arrow */}
              </ListItemButton>
            </StyledListItem>
            
            {/* Removed submenu section */}
          </React.Fragment>
        ))}
      </List>

      {/* Spacer so Admin + copyright stay at bottom and are always visible */}
      <Box sx={{ flexGrow: 1, minHeight: 8 }} />

      {/* Admin — above copyright, only for admins */}
      {!adminLoading && isAdmin && (
        <List component="nav" sx={{ px: 1, py: 0, flexShrink: 0 }}>
          <Tooltip title="Admin dashboard — processing costs & usage" placement="right">
            <StyledListItem disablePadding active={location.pathname.startsWith('/admin')} isSubItem={false}>
              <ListItemButton
                onClick={() => handleNavigation('/admin')}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: theme => alpha(theme.palette.primary.main, 0.08),
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease-in-out',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: '48px' }}>
                  <SecurityIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Admin"
                  secondary="Costs & usage"
                  primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: location.pathname.startsWith('/admin') ? 600 : 500 }}
                  secondaryTypographyProps={{ fontSize: '0.82rem', color: 'text.secondary', sx: { mt: 0.5, lineHeight: 1.3, display: 'block' } }}
                />
              </ListItemButton>
            </StyledListItem>
          </Tooltip>
        </List>
      )}

      <Divider sx={{ flexShrink: 0 }} />

      {/* Copyright Notice */}
      <Box sx={{ p: 2, textAlign: 'center', flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          © 2026 Suprie, LLC. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { lg: `calc(100% - ${drawerWidth}px)` },
          ml: { lg: `${drawerWidth}px` },
          // Remove bgcolor, color, boxShadow, borderBottom, borderColor - let theme handle it
          maxWidth: { lg: `calc(1440px - ${drawerWidth}px)` },
          right: { lg: 'auto' },
          minHeight: theme => theme.mixins.toolbar.minHeight,
          borderRadius: 0, // Make header rectangular
          zIndex: 1200, // Ensure it stays above content
          // Apply safe area insets for devices with notches
          ...getSafeAreaTop(),
          '& .MuiToolbar-root': {
            minHeight: `${64}px !important`,
            height: '64px',
            padding: '0 16px',
            paddingLeft: { xs: 'calc(16px + env(safe-area-inset-left))', lg: '16px' },
            paddingRight: { xs: 'calc(16px + env(safe-area-inset-right))', lg: '16px' },
            borderRadius: 0, // Ensure toolbar is also rectangular
          }
        }}
      >
        <Toolbar sx={{ 
          minHeight: '64px !important', 
          height: '64px',
          padding: '0 !important',
          paddingLeft: '16px !important',
          paddingRight: '16px !important'
        }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { xs: 'none', sm: 'inline-flex', lg: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Suprie Logo (clickable, navigates to home) */}
          {!hideLogo && (
            <Box 
              onClick={() => navigate('/')}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mr: { xs: 2, sm: 3 },
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8
                },
                transition: 'opacity 0.2s'
              }}
            >
              <Logo />
            </Box>
          )}
          
          {/* Pet Switcher - Show when pet is selected */}
          {selectedPet && (
            <Tooltip title="Switch Pet">
              <Button
                onClick={handlePetMenuOpen}
                sx={{
                  color: 'inherit',
                  textTransform: 'none',
                  borderRadius: 2,
                  px: { xs: 0.5, sm: 1.5 },
                  py: 0.5,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.action.hover, 0.08),
                  },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: { xs: 0.25, sm: 0.5 },
                  mr: { xs: 1, sm: 2 },
                  minWidth: 'auto',
                }}
              >
                <Avatar 
                  src={selectedPet.imageUrl} 
                  alt={selectedPet.name}
                  sx={{ 
                    width: { xs: 32, sm: 36 }, 
                    height: { xs: 32, sm: 36 },
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  <PetsIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </Avatar>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontWeight: 600, 
                      fontSize: { xs: '0.6rem', sm: '0.7rem' },
                      lineHeight: 1,
                    }}
                  >
                    {selectedPet.name}
                  </Typography>
                  <ChevronRightIcon sx={{ fontSize: 12, transform: 'rotate(90deg)' }} />
                </Box>
              </Button>
            </Tooltip>
          )}
          
          <Box sx={{ flexGrow: 1 }} />

          {/* Theme toggle — to the left of notifications */}
          <Tooltip title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton onClick={toggleTheme} aria-label="Toggle theme" color="inherit" sx={{ mr: 0.5 }}>
              {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <NotificationBell size="medium" />
          
          {/* User profile */}
          <Tooltip title="Account">
            <IconButton
              edge="end"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar 
                alt={currentUser?.displayName || ''} 
                src={currentUser?.photoURL || ''} 
                sx={{ width: 32, height: 32 }}
              />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      
      {/* Pet Switcher Menu */}
      <Menu
        anchorEl={petMenuAnchor}
        open={isPetMenuOpen}
        onClose={handlePetMenuClose}
        sx={{
          mt: '45px',
          '& .MuiPaper-root': {
            backdropFilter: 'blur(10px)',
            backgroundColor: theme => alpha(theme.palette.background.paper, 0.9),
            borderRadius: 2,
            border: '1px solid',
            borderColor: theme => alpha(theme.palette.divider, 0.1),
            boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.15)}`,
            minWidth: 220,
          }
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {pets.length > 1 ? 'SWITCH PET' : 'CURRENT PET'}
          </Typography>
        </Box>
        {pets.map((pet) => (
          <MenuItem
            key={pet.id}
            onClick={() => handlePetSwitch(pet)}
            selected={selectedPet?.id === pet.id}
            sx={{
              py: 1.5,
              '&.Mui-selected': {
                bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
              }
            }}
          >
            <Avatar
              src={pet.imageUrl}
              alt={pet.name}
              sx={{ width: 32, height: 32, mr: 1.5 }}
            >
              <PetsIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {pet.name}
                </Typography>
                <Tooltip title={`Edit ${pet.name}'s profile`} placement="top">
                  <IconButton
                    size="small"
                    aria-label={`Edit ${pet.name}'s profile`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePetMenuClose();
                      navigate(`/pets/edit/${pet.id}`);
                    }}
                    sx={{
                      p: 0.25,
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main', bgcolor: theme => alpha(theme.palette.primary.main, 0.08) },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {pet.breed || pet.species}
              </Typography>
            </Box>
            {selectedPet?.id === pet.id && (
              <Badge color="primary" variant="dot" sx={{ ml: 1 }} />
            )}
          </MenuItem>
        ))}
        <Divider sx={{ my: 1 }} />
        <MenuItem onClick={() => { handlePetMenuClose(); navigate('/pets/add'); }}>
          <PetsIcon sx={{ mr: 1.5, fontSize: '1.25rem', color: 'primary.main' }} />
          <Typography color="primary.main" sx={{ fontWeight: 600 }}>
            Add New Pet
          </Typography>
        </MenuItem>
      </Menu>

      {/* Profile menu */}
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        sx={{
          mt: '45px',
          '& .MuiPaper-root': {
            backdropFilter: 'blur(10px)',
            backgroundColor: theme => alpha(theme.palette.background.paper, 0.85),
            borderRadius: 2,
            border: '1px solid',
            borderColor: theme => alpha(theme.palette.divider, 0.1),
            boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`
          }
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => { handleMenuClose(); navigate('/profile-settings'); }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
            <Typography>Profile & Settings</Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LogoutIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
            <Typography>Logout</Typography>
          </Box>
        </MenuItem>
      </Menu>
      
      {/* Side drawer */}
      <Box
        component="nav"
        sx={{ width: { lg: drawerWidth }, flexShrink: { lg: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRadius: 0, // Make drawer rectangular with no rounded corners
              // Remove backgroundColor, borderRight, borderColor, backdropFilter - let theme handle it
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              position: 'fixed', // Ensure drawer stays in place
              top: 0,
              left: 0,
              height: '100vh',
              zIndex: 1100, // Below AppBar but above content
              borderRadius: 0, // Make drawer rectangular with no rounded corners
              // Remove backgroundColor, borderRight, borderColor, backdropFilter - let theme handle it
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          pl: { xs: 2, md: 3 },
          pr: { xs: 2, md: 3 },
          pb: { xs: 'calc(80px + env(safe-area-inset-bottom))', lg: 0 }, // Bottom padding for mobile nav
          width: { lg: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'transparent',
          maxWidth: { lg: `calc(1440px - ${drawerWidth}px)` },
          marginLeft: { lg: 0 },
          marginRight: { lg: 0 },
          overflow: 'auto',
          position: 'relative'
        }}
      >
        <Box sx={{ height: '64px' }} /> {/* Exact spacer for fixed AppBar */}
        <EmailVerificationBanner />
        <AnimatedOutlet />
        <AddToHomeScreen />
        <OfflineIndicator />
        <MobileBottomNavigation />
      </Box>
    </Box>
  );
};

export default Layout;